import { useInfiniteQuery } from 'react-query';

import Api from '../Services/Api';
import { Post } from '../Utils/Types/Posts';
import useCurrentUser from './useCurrentUser';

interface Page {
  data: {
    posts: Post[];
    paging: any;
  };
}

interface PostsQueryResponse {
  pages: Page[];
  paging: any;
  postsCount: number;
  hasNextPage: boolean;
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: any;
  fetchNextPage: () => void;
}

const usePostsInfiniteQuery = (key: any | any[], params: any): PostsQueryResponse => {
  const { space, enabled = true, post, ...rest } = params;
  // Note: Posts return permissions based on the current user
  // so we need to refetch the post when the user changes
  const { currentUser } = useCurrentUser();

  const {
    authorId = '',
    extraParams,
    iVotedFor,
    limit,
    originalPostId,
    page = 0,
    parentId = [],
    search = '',
    sort,
    status,
    tags = [],
    types = [],
    visibilities = [],
  } = rest;

  const queryParams = {
    authorId,
    extraParams,
    iVotedFor,
    limit,
    originalPostId,
    page,
    parentId,
    search,
    sort,
    status,
    tags,
    types,
    visibilities,
  };

  const queryKeys = [
    ...(Array.isArray(key) ? key : [key]),
    currentUser?.id,
    post,
    ...Object.values(queryParams),
  ];

  const { data, ...result } = useInfiniteQuery(
    queryKeys,
    ({ pageParam = page }) => Api.getPosts({ ...queryParams, page: pageParam }),
    {
      enabled: enabled && (!!space?.id || !!post?.id),
      keepPreviousData: true,
      getNextPageParam: (lastPage: any, pages: any) =>
        pages.length - 1 === lastPage.data.paging.total_pages ? null : lastPage.data.paging.page,
      refetchOnWindowFocus: false,
    },
  );

  const pages = (data?.pages || []) as Page[];
  const paging = pages[pages.length - 1]?.data?.paging || { total: 0, total_pages: 0 };

  return {
    ...result,
    pages,
    paging,
    postsCount: paging.total || 0,
    hasNextPage: paging.page < paging.total_pages,
  };
};

export default usePostsInfiniteQuery;
